/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getNormativasClientePaginado, exportarNormativasExcel } from "../../../componentes/http/actions/DocumentoAction";
import { getClientesUsuario } from "../../../componentes/http/actions/UsuarioAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Button,
  CardActions,
  CircularProgress,
  Autocomplete,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import DetalleNormativaClienteModal from "../../../componentes/modal/Normativa/DetalleNormativaClienteModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from '@mui/icons-material/Assignment';
import TaskIcon from '@mui/icons-material/Task';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DownloadIcon from '@mui/icons-material/Download';
import DataLoaderCliente from "../../../componentes/data/DataLoaderCliente";
import { useNavigate } from 'react-router-dom';

// Clave para el sessionStorage
const FILTERS_STORAGE_KEY = 'gestorNormativaUsuarioFilters';

// Función para guardar filtros en sessionStorage
const saveFiltersToStorage = (filters) => {
  try {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Error al guardar filtros:', error);
  }
};

// Función para cargar filtros desde sessionStorage
const loadFiltersFromStorage = () => {
  try {
    const saved = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Convertir las fechas de string a Date
      if (parsed.startDate) parsed.startDate = new Date(parsed.startDate);
      if (parsed.endDate) parsed.endDate = new Date(parsed.endDate);
      return parsed;
    }
  } catch (error) {
    console.error('Error al cargar filtros:', error);
  }
  return null;
};

const GestorNormativaUsuario = () => {
  const token = localStorage.getItem("token");
  const decoded = jwtDecode(token);
  const idUsuario = decoded["oid"];
  
  // Cargar filtros guardados al iniciar
  const savedFilters = loadFiltersFromStorage();
  
  // Estados para datos y UI
  const navigate = useNavigate();
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [, setIsLoadingClientes] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isLoadingNormativas, setIsLoadingNormativas] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [openDetalleModal, setOpenDetalleModal] = useState(false);
  const [normativaSeleccionado, setNormativaSeleccionado] = useState(null);
  
  // Estados para paginación con valores guardados
  const [page, setPage] = useState(savedFilters?.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(savedFilters?.rowsPerPage || 10);
  
  // Estados para filtros con valores guardados
  const [searchTerm, setSearchTerm] = useState(savedFilters?.searchTerm || "");
  const [filterCliente, setFilterCliente] = useState(savedFilters?.filterCliente || "todos");
  const [startDate, setStartDate] = useState(savedFilters?.startDate || null);
  const [endDate, setEndDate] = useState(savedFilters?.endDate || null);
  
  // Estados para opciones de filtros y UI
  const [clientes, setClientes] = useState([]);
  const [multipleClients, setMultipleClients] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const [loadedData, setLoadedData] = useState({
    organismos: [],
    tiposDocumento: [],
    estadosTramitacion: [],
    rubros: [],
    clasificaciones: [],
    clientes: [],
  });

  const [selectedFilters, setSelectedFilters] = useState(savedFilters?.selectedFilters || {
    organismo: null,
    tipoDocumento: null,
    estadoTramitacion: null,
    rubro: null,
  });

  const handleDataLoaded = useCallback((data) => {
    setLoadedData(data);
    
    // Si hay filtros guardados, restaurar las referencias de los objetos
    if (savedFilters?.selectedFilters) {
      const restoredFilters = { ...savedFilters.selectedFilters };
      
      // Restaurar cada filtro buscando el objeto correspondiente en los datos cargados
      if (savedFilters.selectedFilters.organismo) {
        restoredFilters.organismo = data.organismos.find(
          o => o.id === savedFilters.selectedFilters.organismo.id
        ) || null;
      }
      if (savedFilters.selectedFilters.tipoDocumento) {
        restoredFilters.tipoDocumento = data.tiposDocumento.find(
          t => t.id === savedFilters.selectedFilters.tipoDocumento.id
        ) || null;
      }
      if (savedFilters.selectedFilters.estadoTramitacion) {
        restoredFilters.estadoTramitacion = data.estadosTramitacion.find(
          e => e.id === savedFilters.selectedFilters.estadoTramitacion.id
        ) || null;
      }
      if (savedFilters.selectedFilters.rubro) {
        restoredFilters.rubro = data.rubros.find(
          r => r.id === savedFilters.selectedFilters.rubro.id
        ) || null;
      }
      
      setSelectedFilters(restoredFilters);
    }
  }, []);

  // Guardar filtros cada vez que cambien
  useEffect(() => {
    const filtersToSave = {
      searchTerm,
      filterCliente,
      startDate,
      endDate,
      page,
      rowsPerPage,
      selectedFilters,
    };
    saveFiltersToStorage(filtersToSave);
  }, [searchTerm, filterCliente, startDate, endDate, page, rowsPerPage, selectedFilters]);

  // Función mejorada para obtener el color del semáforo según la configuración del cliente
  const getSemaforoColor = (cantidad, semaforoInfo, clienteId, tipoServicio = 'Obligaciones') => {
    if (semaforoInfo && semaforoInfo[clienteId] && semaforoInfo[clienteId].tieneConfiguracion) {
      const config = semaforoInfo[clienteId];
      
      // Verificar si el semáforo aplica a este tipo de servicio
      if (config.tipoAplicacion === tipoServicio || config.tipoAplicacion === 'Ambos') {
        // Retornar el color calculado desde el backend
        return tipoServicio === 'Compromisos' ? config.colorCompromisos : config.colorObligaciones;
      }
    }
    
    // Fallback a la lógica original si no hay configuración de semáforo
    if (cantidad === 0) return theme.palette.success.main;
    if (cantidad <= 2) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const renderSemaforoObligaciones = (cantidadObligaciones, documento) => {
    const clienteId = documento.clientes && documento.clientes.length > 0 
      ? documento.clientes[0].id 
      : null;
    if (documento.semaforoInfo && documento.semaforoInfo[clienteId] && 
        documento.semaforoInfo[clienteId].tieneConfiguracion) {
      const config = documento.semaforoInfo[clienteId];

      // Solo mostrar semáforo si aplica a obligaciones
      if (config.tipoAplicacion === 'Obligaciones' || config.tipoAplicacion === 'Ambos') {
        const color = getSemaforoColor(cantidadObligaciones, documento.semaforoInfo, clienteId, 'Obligaciones');
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <FiberManualRecordIcon 
              sx={{ 
                color: color,
                fontSize: '1rem'
              }} 
            />
            <Typography variant="body2">
              {cantidadObligaciones}
            </Typography>
          </Box>
        );
      }
    }
    return(
      <Typography variant="body2">
        {cantidadObligaciones}
      </Typography>
    )
  };

  // Función para renderizar el semáforo de compromisos
  const renderSemaforoCompromisos = (cantidadCompromisos, documento) => {
    const clienteId = documento.clientes && documento.clientes.length > 0 
      ? documento.clientes[0].id 
      : null;
      
    if (documento.semaforoInfo && documento.semaforoInfo[clienteId] && 
        documento.semaforoInfo[clienteId].tieneConfiguracion) {
      const config = documento.semaforoInfo[clienteId];
      
      // Solo mostrar semáforo si aplica a compromisos
      if (config.tipoAplicacion === 'Compromisos' || config.tipoAplicacion === 'Ambos') {
        const color = getSemaforoColor(cantidadCompromisos, documento.semaforoInfo, clienteId, 'Compromisos');
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <FiberManualRecordIcon 
              sx={{ 
                color: color,
                fontSize: '1rem'
              }} 
            />
            <Typography variant="body2">
              {cantidadCompromisos}
            </Typography>
          </Box>
        );
      }
    }
    
    // Renderizado normal sin semáforo si no hay configuración o no aplica
    return (
      <Typography variant="body2">
        {cantidadCompromisos}
      </Typography>
    );
  };

  // Función para obtener el ícono según el servicio
  const getServiceIcon = (servicio) => {
    switch (servicio) {
      case 'Compromisos':
        return <AssignmentIcon fontSize="small" />;
      case 'Obligaciones':
        return <TaskIcon fontSize="small" />;
      case 'Enlace Resumen':
        return <AccountBalanceIcon fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  // Función para obtener el color según el servicio
  const getServiceColor = (servicio) => {
    switch (servicio) {
      case 'Compromisos':
        return 'primary';
      case 'Obligaciones':
        return 'primary';
      case 'Enlace Resumen':
        return 'info';
      default:
        return 'primary';
    }
  };

  // Función genérica para manejar cualquier servicio
  const handleVerServicio = (servicio, idNormativa) => {
    const rutaServicio = servicio.toLowerCase().replace(' ', '-');
    navigate(`/usuario/${rutaServicio}/normativa/${idNormativa}`);
  };

  // Obtener inicialmente la data
  useEffect(() => {
    fetchClientes();
    fetchNormativasPaginadas();
  }, []);
  
  useEffect(() => {
    setMultipleClients(clientes && clientes.length > 1);
  }, [clientes]);

  useEffect(() => {
    if (clientes && clientes.length > 0) {
      // Obtener todos los servicios únicos disponibles para los clientes del usuario
      const serviciosUnicos = clientes.reduce((servicios, cliente) => {
        if (cliente.servicios && Array.isArray(cliente.servicios)) {
          cliente.servicios.forEach(servicio => {
            if (!servicios.includes(servicio)) {
              servicios.push(servicio);
            }
          });
        }
        return servicios;
      }, []);

      setServiciosDisponibles(serviciosUnicos);
    }
  }, [clientes]);

  const fetchNormativasPaginadas = useCallback(() => {
    setIsLoadingNormativas(true);
    
    // Extraer valores correctos de los filtros seleccionados
    const filtros = {
      searchTerm: searchTerm,
      organismo: selectedFilters.organismo?.nombre || null, 
      tipoDocumento: selectedFilters.tipoDocumento?.nombre || null,
      estadoTramitacion: selectedFilters.estadoTramitacion?.nombre || null,
      cliente: filterCliente !== 'todos' ? filterCliente : null,
      rubro: selectedFilters.rubro?.nombre || null,
      fechaDesde: startDate,
      fechaHasta: endDate
    };
    
    getNormativasClientePaginado(token, idUsuario, page + 1, rowsPerPage, filtros)
      .then((response) => {        
        if (!response) {
          setDocumentos([]);
          setTotalCount(0);
          return;
        }
        
        if (response.items && Array.isArray(response.items)) {
          setDocumentos(response.items);
          setTotalCount(response.totalCount || 0);
        } else {
          setDocumentos([]);
          setTotalCount(0);
        }
      })
      .catch((error) => {
        console.error("Error al cargar normativas:", error);
        setDocumentos([]);
        setTotalCount(0);
      })
      .finally(() => {
        setIsLoadingNormativas(false);
        setInitialLoadComplete(true);
      });
  }, [page, rowsPerPage, searchTerm, filterCliente, selectedFilters, startDate, endDate, token, idUsuario]);

  useEffect(() => {
    fetchNormativasPaginadas();
  }, [fetchNormativasPaginadas]);

  const handleFilterChange = (field, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const fetchClientes = () => {
    setIsLoadingClientes(true);
    getClientesUsuario(token, idUsuario)
      .then((response) => {
        if (response && Array.isArray(response)) {
          setClientes(response);
        } else {
          setClientes([]);
          console.error("Respuesta no válida para clientes:", response);
        }
      })
      .catch((error) => {
        setClientes([]);
      })
      .finally(() => {
        setIsLoadingClientes(false);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDetalleModal = useCallback((normativa) => {
    setNormativaSeleccionado(normativa);
    setOpenDetalleModal(true);
  }, []);

  const handleCloseDetalleModal = useCallback(() => {
    setOpenDetalleModal(false);
    setNormativaSeleccionado(null);
  }, []);

  const handleExportarExcel = () => {
    setIsDownloading(true);
    
    // Crear objeto con los filtros correctamente nombrados
    const filtros = {
      nombre: searchTerm,
      organismo: selectedFilters.organismo?.nombre || null,
      tipoDocumento: selectedFilters.tipoDocumento?.nombre || null,
      estadoTramitacion: selectedFilters.estadoTramitacion?.nombre || null,
      cliente: filterCliente !== 'todos' ? filterCliente : null,
      fechaDesde: startDate,
      fechaHasta: endDate
    };
    
    exportarNormativasExcel(token, idUsuario, filtros)
      .then((blob) => {
        console.log("Tamaño del blob recibido:", blob.size);
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Normativas_${new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error al exportar Excel:", error);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  // Manejadores de filtros
  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleClienteFilterChange = (e) => {
    setFilterCliente(e.target.value);
    setPage(0);
  };

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    setPage(0);
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
    setPage(0);
  };

  const handleResetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
    setFilterCliente("todos");
    
    setSelectedFilters({
      organismo: null,
      tipoDocumento: null,
      estadoTramitacion: null,
      rubro: null,
    });
    
    setPage(0);
    
    // Limpiar también el sessionStorage
    sessionStorage.removeItem(FILTERS_STORAGE_KEY);
  };

  const renderNoDataMessage = () => (
    <TableContainer component={Paper}>
      <Box p={3} sx={{ width: "100%" }}>
        <Typography variant="body1" color="text.secondary">
          No se encontraron normativas con los criterios de búsqueda actuales.
        </Typography>
      </Box>
    </TableContainer>
  );

  // Definir columnas para pantallas medianas y grandes
  const tableColumns = useMemo(() => {
    const baseColumns = [
      { id: 'nombre', label: 'Nombre', minWidth: 200, maxWidth: 300 },
      { id: 'organismoEmisor', label: 'Organismo', minWidth: 120, hideOnBreakpoint: 'md' },
      { id: 'tipoDocumento', label: 'Tipo', minWidth: 120, hideOnBreakpoint: 'md' },
      { id: 'estadoTramitacion', label: 'Estado', minWidth: 120 },
      { id: 'fechaPublicacion', label: 'Fecha Publicación', minWidth: 100, 
        format: (value) => value ? new Date(value).toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }) : '' 
      },
    ];

    // Agregar columna de clientes solo si hay múltiples clientes
    if (multipleClients) {
      baseColumns.push({
        id: 'clientes', 
        label: 'Clientes', 
        minWidth: 150,
        hideOnBreakpoint: 'md',
        format: (value) => value && Array.isArray(value) 
          ? value.map(cliente => cliente.nombre).join(', ') 
          : ''
      });
    }

    // Agregar columnas de compromisos y obligaciones si los servicios están disponibles
    if (serviciosDisponibles.includes('Compromisos')) {
      baseColumns.push({
        id: 'compromisos',
        label: 'Compromisos',
        minWidth: 100,
        align: 'center',
        hideOnBreakpoint: 'md',
        format: (value, documento) => renderSemaforoCompromisos(
          value && Array.isArray(value) ? value.length : 0,
          documento
        )
      });
    }

    if (serviciosDisponibles.includes('Obligaciones')) {
      baseColumns.push({
        id: 'obligaciones',
        label: 'Obligaciones',
        minWidth: 110,
        align: 'center',
        hideOnBreakpoint: 'md',
        format: (value, documento) => renderSemaforoObligaciones(
          value && Array.isArray(value) ? value.length : 0,
          documento
        )
      });
    }

    // Agregar columna de acciones
    baseColumns.push({ 
      id: 'acciones', 
      label: 'Acciones', 
      minWidth: Math.max(120, serviciosDisponibles.length * 40 + 80), 
      align: 'center' 
    });

    return baseColumns;
  }, [multipleClients, serviciosDisponibles]);

  // Filtrar columnas según el tamaño de pantalla
  const visibleColumns = useMemo(() => 
    tableColumns.filter(column => {
      if (column.hideOnBreakpoint === 'md' && isSmall) return false;
      return true;
    }), 
  [tableColumns, isSmall]);

  // Renderizar botones de servicios
  const renderServiceButtons = (documento, isMobile = false) => {
    const serviciosMostrar = serviciosDisponibles.filter(
      servicio => servicio.toLowerCase() !== 'semaforo' && servicio.toLowerCase() !== 'enlaceresumen' && servicio.toLowerCase() !== 'integracion austranetgrc'
    );
    
    return serviciosMostrar.map((servicio) => {
      const buttonProps = {
        size: "small",
        color: getServiceColor(servicio),
        onClick: () => handleVerServicio(servicio, documento.id)
      };
      
      if (isMobile) {
        return (
          <Button
            key={servicio}  
            {...buttonProps}
            variant="outlined"
            startIcon={getServiceIcon(servicio)}
            sx={{ ml: 1 }}
          >
            {servicio}
          </Button>
        );
      } else {
        return (
          <Tooltip key={servicio} title={`Ver ${servicio.toLowerCase()}`}>
            <IconButton 
              key={servicio}
              {...buttonProps}
            >
              {getServiceIcon(servicio)}
            </IconButton>
          </Tooltip>
        );
      }
    });
  };

  // Renderizar las tarjetas para móviles
  const renderMobileCard = (documento) => (
    <Card key={documento.id} sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ 
          wordBreak: "break-word",
          mb: 1,
          fontSize: "1rem"
        }}>
          {documento.nombre}
        </Typography>
        <Divider sx={{ my: 1 }} />
        
        <Grid container spacing={1}>          
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Estado:
            </Typography>
            <Typography variant="body2" gutterBottom>
              {documento.estadoTramitacion}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Organismo:
            </Typography>
            <Typography variant="body2" gutterBottom>
              {documento.organismoEmisor}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Tipo:
            </Typography>
            <Typography variant="body2" gutterBottom>
              {documento.tipoDocumento}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Fecha:
            </Typography>
            <Typography variant="body2" gutterBottom>
              {documento.fechaPublicacion ? new Date(documento.fechaPublicacion).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              }) : ''}
            </Typography>
          </Grid>

          {/* Mostrar compromisos si el servicio está disponible */}
          {serviciosDisponibles.includes('Compromisos') && (
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Compromisos:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {renderSemaforoCompromisos(documento.compromisos ? documento.compromisos.length : 0, documento)}
              </Box>
            </Grid>
          )}

          {/* Mostrar obligaciones con semáforo si el servicio está disponible */}
          {serviciosDisponibles.includes('Obligaciones') && (
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Obligaciones:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {renderSemaforoObligaciones(documento.obligaciones ? documento.obligaciones.length : 0, documento)}
              </Box>
            </Grid>
          )}
          
          {multipleClients && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Clientes:
              </Typography>
              <Typography variant="body2" gutterBottom>
                {documento.clientes && Array.isArray(documento.clientes) 
                  ? documento.clientes.map((cliente) => cliente.nombre).join(", ")
                  : ""}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions sx={{ 
        display: 'flex', 
        justifyContent: 'flex-start',
        padding: 1,
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => handleOpenDetalleModal(documento)}
          startIcon={<VisibilityIcon />}
        >
          Detalle
        </Button>
        
        {documento.url && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            component="a"
            href={documento.url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DescriptionIcon />}
          >
            Documento
          </Button>
        )}
        
        {/* Renderizar botones de servicios dinámicamente */}
        {renderServiceButtons(documento, true)}
      </CardActions>
    </Card>
  );
  
  return (
    <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <DataLoaderCliente onDataLoaded={handleDataLoaded} />
      <Grid item>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Normativas disponibles
        </Typography>
      </Grid>
      
      {/* Sección de filtros mejorada */}
      <Grid item>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filtros de búsqueda
          </Typography>
          <Grid 
            container 
            spacing={2} 
            alignItems="flex-start"
          >
            {/* Filtro por nombre */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                fullWidth
                label="Buscar por nombre"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchTermChange}
              />
            </Grid>
            
            {/* Filtro por cliente */}
            {multipleClients && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Cliente</InputLabel>
                  <Select
                    value={filterCliente}
                    onChange={handleClienteFilterChange}
                    label="Cliente"
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    {clientes && Array.isArray(clientes) && clientes.map((cliente) => (
                      <MenuItem key={cliente.id} value={cliente.nombre}>
                        {cliente.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Organismo emisor */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  options={loadedData.organismos || []}
                  getOptionLabel={(option) => option.nombre || ""}
                  renderInput={(params) => <TextField {...params} label="Organismo emisor" size="small" />}
                  value={selectedFilters.organismo}
                  onChange={(event, newValue) => handleFilterChange("organismo", newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </FormControl>
            </Grid>
            
            {/* Tipo de documento */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  options={loadedData.tiposDocumento || []}
                  getOptionLabel={(option) => option.nombre || ""}
                  renderInput={(params) => <TextField {...params} label="Tipo de documento" size="small" />}
                  value={selectedFilters.tipoDocumento}
                  onChange={(event, newValue) => handleFilterChange("tipoDocumento", newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </FormControl>
            </Grid>
            
            {/* Estado de tramitación */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  options={loadedData.estadosTramitacion || []}
                  getOptionLabel={(option) => option.nombre || ""}
                  renderInput={(params) => <TextField {...params} label="Estado de tramitación" size="small" />}
                  value={selectedFilters.estadoTramitacion}
                  onChange={(event, newValue) => handleFilterChange("estadoTramitacion", newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </FormControl>
            </Grid>
            
            {/* Rubros */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  options={loadedData.rubros || []}
                  getOptionLabel={(option) => option.nombre || ""}
                  renderInput={(params) => <TextField {...params} label="Rubro" size="small" />}
                  value={selectedFilters.rubro}
                  onChange={(event, newValue) => handleFilterChange("rubro", newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                />
              </FormControl>
            </Grid>

            {/* Fecha desde */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  sx={{ width: "100%" }}
                  format="dd/MM/yyyy"
                  label="Fecha Desde"
                  value={startDate}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
            
            {/* Fecha hasta */}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  sx={{ width: "100%" }}
                  format="dd/MM/yyyy"
                  label="Fecha Hasta"
                  value={endDate}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>
            
            {/* Botones de acción */}
            <Grid item xs={12} container spacing={1} sx={{ mt: 1 }}>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleResetFilters}
                  size="small"
                >
                  Limpiar filtros
                </Button>
              </Grid>
              
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  onClick={handleExportarExcel}
                  startIcon={<DownloadIcon />}
                  disabled={isDownloading}
                  size="small"
                >
                  {isDownloading ? <CircularProgress size={24} /> : "Excel"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      
      {/* Sección de tabla/tarjetas según tamaño de pantalla */}
      <Grid item>
        {(!initialLoadComplete || isLoadingNormativas) ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : documentos && documentos.length > 0 ? (
          <>
            {/* Vista de tarjetas para móviles */}
            {isMobile ? (
             <Box sx={{ mt: 2 }}>
               {documentos.map(renderMobileCard)}
             </Box>
           ) : (
             /* Vista de tabla para pantallas medianas y grandes */
             <TableContainer component={Paper} sx={{ overflow: "auto" }}>
               <Table stickyHeader size="small">
                 <TableHead>
                   <TableRow>
                     {visibleColumns.map((column) => (
                       <TableCell 
                         key={column.id} 
                         align={column.align || 'left'}
                         sx={{ 
                           minWidth: column.minWidth,
                           padding: '10px 8px',
                           whiteSpace: 'nowrap',
                           fontWeight: 'bold'
                         }}
                       >
                         {column.label}
                       </TableCell>
                     ))}
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {documentos.map((documento) => (
                     <TableRow 
                       key={documento.id}
                       hover
                       sx={{
                         '&:nth-of-type(odd)': {
                           backgroundColor: theme.palette.action.hover,
                         },
                       }}
                     >
                       {visibleColumns.map((column) => {
                         if (column.id === 'acciones') {
                           return (
                             <TableCell 
                               key={column.id} 
                               align={column.align || 'left'}
                               sx={{ padding: '4px 8px' }}
                             >
                               <Box 
                                 sx={{ 
                                   display: 'flex',
                                   flexDirection: 'row',
                                   justifyContent: 'center',
                                   gap: 0.5,
                                   flexWrap: 'wrap'
                                 }}
                               >
                                 <Tooltip title="Ver detalle">
                                   <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenDetalleModal(documento)}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                
                                {documento.url && (
                                  <Tooltip title="Ver documento">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      component="a"
                                      href={documento.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <DescriptionIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                {/* Renderizar botones de servicios dinámicamente */}
                                {renderServiceButtons(documento, false)}
                              </Box>
                            </TableCell>
                          );
                        }
                        
                        const value = documento[column.id];
                        return (
                          <TableCell 
                            key={column.id} 
                            align={column.align || 'left'}
                            sx={{ 
                              padding: '8px',
                              maxWidth: column.id === 'nombre' ? '250px' : '150px',
                              whiteSpace: "nowrap", 
                              overflow: "hidden", 
                              textOverflow: "ellipsis"
                            }}
                          >
                            {column.format ? column.format(value, documento) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Paginación común para ambas vistas */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      ) : (
        renderNoDataMessage()
      )}
    </Grid>
    
    {/* Modal de detalle */}
    {openDetalleModal && (
      <DetalleNormativaClienteModal
        open={openDetalleModal}
        onClose={handleCloseDetalleModal}
        normativa={normativaSeleccionado}
        isLoading={isLoadingNormativas}
      />
    )}
  </Grid>
);
};

export default GestorNormativaUsuario;